import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Drawer, Avatar, Dropdown, Button, theme } from 'antd';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  LogoutOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { visibleMenu, visiblePages, categoryOf } from '@config/sidebarConfig';
import api from '../../libs/api';

const { Header, Sider, Content } = Layout;

const MOBILE_BREAKPOINT = 768;

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT
  );

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return isMobile;
}

// Which pages this caller sees, and which categories survive having their
// pages filtered, both come from sidebarConfig — the login landing page has to
// answer the same question and the two must not drift.

// Convert our config to Ant Design Menu items format
function toAntMenuItems(items) {
  return items.map((item) => {
    const Icon = item.icon;
    return {
      key: item.key,
      icon: <Icon />,
      label: item.label,
      ...(item.children ? { children: toAntMenuItems(item.children) } : {}),
    };
  });
}

/**
 * Which categories are open.
 *
 * Controlled rather than left to antd, because the rule is not "remember what
 * was clicked": every category starts shut, and the one holding the page being
 * looked at opens itself. Left uncontrolled, antd opens whichever submenu
 * contains the selected key and the rest of the rule cannot be expressed.
 */
function useOpenCategories(activeKey) {
  const category = categoryOf(activeKey);
  const [openKeys, setOpenKeys] = useState(() => (category ? [category] : []));
  // Only reacts when the page moves to a different category. Without that
  // guard, collapsing the category you are currently in would spring straight
  // back open on the next render.
  const lastCategory = useRef(category);
  useEffect(() => {
    if (category === lastCategory.current) return;
    lastCategory.current = category;
    if (category) setOpenKeys((prev) => (prev.includes(category) ? prev : [...prev, category]));
  }, [category]);
  return [openKeys, setOpenKeys];
}

// Build key → path mapping
function getKeyPathMap(items, map = {}) {
  items.forEach((item) => {
    if (item.path) {
      map[item.key] = item.path;
    }
    if (item.children) {
      getKeyPathMap(item.children, map);
    }
  });
  return map;
}

// User dropdown items
const userMenuItems = [
  { key: 'profile', icon: <UserOutlined />, label: 'My Profile' },
  { type: 'divider' },
  { key: 'logout', icon: <LogoutOutlined />, label: 'Sign Out', danger: true },
];

// Desktop sidebar content
function SidebarContent({
  collapsed, activeKey, antMenuItems, onMenuClick, openKeys, onOpenChange,
}) {
  return (
    <>
      <div
        style={{
          height: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          padding: collapsed ? '0' : '0 24px',
          gap: 10,
          borderBottom: '1px solid rgba(255,255,255,0.1)',
        }}
      >
        <img
          src="/logocompany.png"
          alt="Powow"
          style={{ width: 34, height: 34, borderRadius: 6, flexShrink: 0 }}
        />
        {!collapsed && (
          <span
            style={{
              color: '#fff',
              fontSize: 15,
              fontWeight: 700,
              whiteSpace: 'nowrap',
            }}
          >
            Powow Group
          </span>
        )}
      </div>
      <Menu
        theme="dark"
        mode="inline"
        selectedKeys={[activeKey]}
        items={antMenuItems}
        onClick={onMenuClick}
        /**
         * openKeys is left off entirely while the rail is collapsed: antd drives
         * the flyouts itself there, and handing it controlled keys as well makes
         * a submenu that will not stay shut.
         */
        {...(collapsed ? {} : { openKeys, onOpenChange: onOpenChange })}
        style={{ background: 'transparent', borderRight: 'none', marginTop: 8 }}
      />
    </>
  );
}

/**
 * Mobile icon-only sidebar.
 *
 * Pages, not categories. This rail is a column of icons with nothing to click
 * open, so a category here would be an icon that does nothing — the pages are
 * flattened out from under the headings instead.
 */
function MobileSidebar({ activeKey, pageItems, onMenuClick }) {
  return (
    <>
      <div
        style={{
          height: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
        }}
      >
        <img
          src="/logocompany.png"
          alt="Powow"
          style={{ width: 34, height: 34, borderRadius: 6 }}
        />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: 8, gap: 4 }}>
        {pageItems.map((item) => {
          const Icon = item.icon;
          const isActive = item.key === activeKey;
          return (
            <div
              key={item.key}
              onClick={() => onMenuClick({ key: item.key })}
              style={{
                width: 48,
                height: 48,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 8,
                cursor: 'pointer',
                color: isActive ? '#fff' : 'rgba(255,255,255,0.65)',
                background: isActive ? '#54A2CC' : 'transparent',
                fontSize: 18,
                transition: 'all 0.2s',
              }}
            >
              <Icon />
            </div>
          );
        })}
      </div>
    </>
  );
}

export default function PageLayout({ children }) {
  const [collapsed, setCollapsed] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = theme.useToken();

  // Close drawer on route change
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  const userRoleType = localStorage.getItem('roleType') || '';
  const [userPermissions, setUserPermissions] = useState(() => {
    try { return JSON.parse(localStorage.getItem('permissions') || '[]'); } catch { return []; }
  });

  // The cached list is written at login, so a permission granted since then is
  // invisible to an open session. Re-read it from the server on mount: a role
  // change should take effect on the next page load, not on the next sign-in.
  useEffect(() => {
    let cancelled = false;
    api.get('/api/auth/profile').then((r) => {
      const fresh = r.data?.data?.permissions;
      if (cancelled || !Array.isArray(fresh)) return;
      if (JSON.stringify(fresh) !== localStorage.getItem('permissions')) {
        localStorage.setItem('permissions', JSON.stringify(fresh));
        setUserPermissions(fresh);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);
  const userName = localStorage.getItem('username') || 'User';
  const firstName = userName.split(' ')[0] || 'User';
  const userInitials = userName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || 'U';

  const filteredItems = useMemo(
    () => visibleMenu(userPermissions, userRoleType),
    [userPermissions, userRoleType]
  );
  const pageItems = useMemo(
    () => visiblePages(userPermissions, userRoleType),
    [userPermissions, userRoleType]
  );

  const antMenuItems = useMemo(() => toAntMenuItems(filteredItems), [filteredItems]);
  const keyPathMap = useMemo(() => getKeyPathMap(filteredItems), [filteredItems]);

  const activeKey = useMemo(() => {
    for (const [key, path] of Object.entries(keyPathMap)) {
      if (location.pathname === path || location.pathname.startsWith(path + '/')) {
        return key;
      }
    }
    return 'dashboard';
  }, [location.pathname, keyPathMap]);

  const handleMenuClick = ({ key }) => {
    const path = keyPathMap[key];
    if (path) {
      navigate(path);
      setDrawerOpen(false);
    }
  };

  const handleUserMenuClick = ({ key }) => {
    if (key === 'profile') {
      navigate('/profile');
      return;
    }
    if (key === 'logout') {
      localStorage.removeItem('token');
      localStorage.removeItem('userId');
      localStorage.removeItem('username');
      localStorage.removeItem('roleType');
      localStorage.removeItem('permissions');
      document.cookie = 'token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      navigate('/login');
    }
  };

  const [openKeys, setOpenKeys] = useOpenCategories(activeKey);
  const sidebarProps = {
    activeKey,
    antMenuItems,
    onMenuClick: handleMenuClick,
    openKeys,
    onOpenChange: setOpenKeys,
  };

  return (
    <Layout style={{ minHeight: '100vh', height: '100vh' }}>
      {/* Mobile: Narrow icon-only Drawer */}
      {isMobile && (
        <Drawer
          placement="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          width={80}
          closable={false}
          className="mobile-sidebar-drawer"
          styles={{
            body: { padding: 0, background: 'linear-gradient(180deg, #2e3650 0%, #262B40 100%)' },
            header: { display: 'none' },
          }}
        >
          <MobileSidebar activeKey={activeKey} pageItems={pageItems} onMenuClick={handleMenuClick} />
        </Drawer>
      )}

      {/* Desktop: Fixed Sider */}
      {!isMobile && (
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          width={260}
          collapsedWidth={80}
          style={{
            background: 'linear-gradient(180deg, #2e3650 0%, #262B40 100%)',
            boxShadow: '2px 0 8px rgba(0,0,0,0.15)',
            position: 'fixed',
            left: 0,
            top: 0,
            bottom: 0,
            zIndex: 100,
            overflow: 'auto',
          }}
        >
          <SidebarContent collapsed={collapsed} {...sidebarProps} />
        </Sider>
      )}

      {/* Main Layout */}
      <Layout
        style={{
          marginLeft: isMobile ? 0 : (collapsed ? 80 : 260),
          transition: 'margin-left 0.2s',
          height: '100vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <Header
          style={{
            height: isMobile ? 56 : 64,
            lineHeight: isMobile ? '56px' : '64px',
            padding: isMobile ? '0 12px' : '0 24px',
            background: token.colorBgContainer,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
            flexShrink: 0,
            zIndex: 99,
          }}
        >
          <Button
            type="text"
            icon={isMobile ? <MenuUnfoldOutlined /> : (collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />)}
            onClick={() => isMobile ? setDrawerOpen(true) : setCollapsed(!collapsed)}
            style={{ fontSize: 18, width: 48, height: 48 }}
          />

          <Dropdown
            menu={{ items: userMenuItems, onClick: handleUserMenuClick }}
            placement="bottomRight"
            trigger={['click']}
          >
            <div
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                cursor: 'pointer', padding: '6px 10px', borderRadius: 8,
              }}
            >
              <Avatar
                size={isMobile ? 32 : 'default'}
                style={{
                  background: 'linear-gradient(135deg, #54A2CC 0%, #3d8ab8 100%)',
                  fontWeight: 600,
                }}
              >
                {userInitials}
              </Avatar>
              {!isMobile && (
                <span style={{ color: token.colorText, fontWeight: 500 }}>
                  {firstName}
                </span>
              )}
            </div>
          </Dropdown>
        </Header>

        {/* Content */}
        <Content
          style={{
            padding: isMobile ? 12 : 24,
            overflow: 'auto',
            flex: 1,
            background: token.colorBgLayout,
          }}
        >
          {children}
        </Content>
      </Layout>
    </Layout>
  );
}
